/**
 * Per-leg overlay layer — renders, at the midpoint of each leg between two
 * mission waypoints:
 *
 *   1. A gimbal-direction arrow rotated to the leg's gimbal yaw (absolute
 *      world-frame, 0=N). Cyan by default; red border ring when the leg
 *      is recording video; red outline when the leg has a validation
 *      error. The arrow is draggable to set yaw — Shift snaps to 5°.
 *   2. A tilt label ("-45°") below the arrow.
 *   3. Small REC + interval badges as inline chips above the arrow.
 *   4. Distance-interval capture points: small dot markers at every Nm
 *      along the leg, when interval shooting is enabled. They make the
 *      planned capture cadence visible at a glance.
 *
 * Click (no drag) opens a Mapbox popup with the same fields as the side
 * panel's leg card, scoped to that leg. Edits go straight to the store
 * and propagate back through the rAF subscription pattern shared by the
 * other dock-demo layers (see DemoMissionMapLayer for the safe pattern).
 *
 * All sources/layers prefixed `dock-demo-leg-*`. Mounted at EditorPage
 * level alongside the other dock-demo layers — never reaches into
 * MapCanvas internals.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import mapboxgl, { type Map as MapboxMap, type MapMouseEvent } from 'mapbox-gl';
import { createPortal } from 'react-dom';
import { useMapStore } from '../../stores';
import { useDockDemoStore } from '../../stores/dockDemoStore';
import type {
  DemoMission,
  DemoLegParams,
  DemoPhotoPayload,
} from './types';
import {
  resolveEffectiveLegParams,
  validateMission,
  type ValidationError,
} from '../../utils/missionValidation';
import './LegOverlayLayer.css';

const SRC_MIDPOINTS = 'dock-demo-leg-mids-src';
const SRC_CAPTURES = 'dock-demo-leg-caps-src';
const SRC_YAW_LINES = 'dock-demo-leg-yaw-lines-src';
const SRC_YAW_TIPS = 'dock-demo-leg-yaw-tips-src';
const LAYER_YAW_LINE = 'dock-demo-leg-yaw-line-lyr';
const LAYER_YAW_TIP_FILL = 'dock-demo-leg-yaw-tip-fill-lyr';
const LAYER_MID_BG = 'dock-demo-leg-mid-bg-lyr';
const LAYER_MID_ARROW = 'dock-demo-leg-mid-arrow-lyr';
const LAYER_MID_LABEL = 'dock-demo-leg-mid-label-lyr';
const LAYER_CAPS = 'dock-demo-leg-caps-lyr';

// Order matters: pinLayersToTop iterates this array calling moveLayer in
// order, which lifts each layer to the very top in turn. The last entry
// ends up topmost. Yaw line under everything; tip arrow above the
// capture dots so it stays clickable; midpoint group on top.
const ALL_LAYERS = [
  LAYER_YAW_LINE,
  LAYER_CAPS,
  LAYER_YAW_TIP_FILL,
  LAYER_MID_BG,
  LAYER_MID_ARROW,
  LAYER_MID_LABEL,
] as const;
const ALL_SOURCES = [SRC_MIDPOINTS, SRC_CAPTURES, SRC_YAW_LINES, SRC_YAW_TIPS] as const;

/** Visual length of the gimbal-direction indicator (line + triangle tip),
 *  in meters. The dashed line covers the first portion; the filled
 *  triangle covers the last YAW_TIP_TRIANGLE_LEN_M and shares its base
 *  with the line's endpoint so they read as one continuous arrow. */
const YAW_LINE_LENGTH_M = 12;
const YAW_TIP_TRIANGLE_LEN_M = 3;
const YAW_TIP_TRIANGLE_HALF_WIDTH_M = 1.5;

/** Which layers are valid drag-targets for rotating the gimbal yaw. The
 *  midpoint marker shell and the tip-triangle fill both act as grab
 *  handles; drag math is identical (bearing from leg midpoint to cursor)
 *  regardless of which one was grabbed.
 *
 *  Key design choice: the tip is a `fill` layer (not a `symbol` layer).
 *  Symbol layers re-position through Mapbox's placement engine, which
 *  runs deferred and lags behind direct-render layers like `line` and
 *  `fill`. During a drag, a symbol-based tip visibly desyncs from the
 *  dashed line; a fill polygon stays glued because both pipelines flush
 *  on the same render pass. */
const DRAG_LAYER_IDS = [LAYER_MID_BG, LAYER_YAW_TIP_FILL] as const;
const PAYLOADS: DemoPhotoPayload[] = ['wide', 'zoom', 'ir'];

const EARTH_RADIUS_M = 6371000;

function haversineM(a: [number, number], b: [number, number]): number {
  const lat1 = (a[1] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2
    + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(x)));
}

function midpoint(a: [number, number], b: [number, number]): [number, number] {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

/**
 * Spherical destination point: travel `distanceM` meters from `origin`
 * along bearing `bearingDeg` (0=N, 90=E, etc.). Used to project the gimbal
 * yaw line so the dashed pointer matches the actual on-the-ground heading
 * — pseudo-linear lng/lat translation would skew badly at high latitudes
 * or for legs near the poles. Geographic accuracy isn't critical for
 * meter-scale lines, but doing it right costs nothing and avoids the
 * "why does my line drift off when I'm at 60° N" support burden later.
 */
function destinationPoint(
  origin: [number, number],
  bearingDeg: number,
  distanceM: number,
): [number, number] {
  const φ1 = (origin[1] * Math.PI) / 180;
  const λ1 = (origin[0] * Math.PI) / 180;
  const θ = (bearingDeg * Math.PI) / 180;
  const δ = distanceM / EARTH_RADIUS_M;
  const sinφ2 = Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ);
  const φ2 = Math.asin(sinφ2);
  const y = Math.sin(θ) * Math.sin(δ) * Math.cos(φ1);
  const x = Math.cos(δ) - Math.sin(φ1) * sinφ2;
  const λ2 = λ1 + Math.atan2(y, x);
  return [
    (((λ2 * 180) / Math.PI + 540) % 360) - 180,
    (φ2 * 180) / Math.PI,
  ];
}

/**
 * Build dashed yaw-direction lines + the matching tip-point features for
 * each leg with an explicit gimbal yaw. Returned as two feature
 * collections so they go into separate sources (line geometry vs point
 * geometry). Only emits when the leg has a yaw value — without one, the
 * midpoint marker shows a "follow aircraft" dot and direction visuals
 * would convey nothing.
 *
 * `recording` and `hasError` are stamped on both features so the tip
 * marker and the dashed line track the midpoint marker's accent colour.
 * `yawDeg` rides on the tip feature so the tip arrow glyph can rotate to
 * match the gimbal direction.
 */
function buildYawLineFeatures(mission: DemoMission | undefined, errors: ValidationError[]) {
  if (!mission || mission.waypoints.length < 2) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  const wps = mission.waypoints;
  const erroredLegs = new Set(
    errors
      .filter((e) => e.scope.kind === 'leg')
      .map((e) => (e.scope as { kind: 'leg'; legIndex: number }).legIndex),
  );
  const features: GeoJSON.Feature<GeoJSON.LineString>[] = [];
  // The line ends at the triangle's base so the two geometries are
  // exactly contiguous — no visible gap and no overlap.
  const lineEndDistanceM = Math.max(0, YAW_LINE_LENGTH_M - YAW_TIP_TRIANGLE_LEN_M);
  for (let i = 0; i < wps.length - 1; i++) {
    const lp = resolveEffectiveLegParams(wps, i);
    if (lp.gimbalYaw === undefined) continue;
    const mid = midpoint(
      [wps[i].lng, wps[i].lat],
      [wps[i + 1].lng, wps[i + 1].lat],
    );
    const lineEnd = destinationPoint(mid, lp.gimbalYaw, lineEndDistanceM);
    features.push({
      type: 'Feature',
      properties: {
        legIndex: i,
        recording: lp.recordVideo ? 1 : 0,
        hasError: erroredLegs.has(i) ? 1 : 0,
      },
      geometry: { type: 'LineString', coordinates: [mid, lineEnd] },
    });
  }
  return { type: 'FeatureCollection' as const, features };
}

/**
 * Build a small filled triangle pointing along the gimbal yaw, with its
 * base flush against the dashed line's endpoint. Returned as Polygon
 * features so the fill layer renders them — fill layers update on the
 * same render pass as line layers, so the tip stays glued to the line
 * during drag (a `symbol` layer would lag because of deferred symbol
 * placement).
 */
function buildYawTipFeatures(mission: DemoMission | undefined, errors: ValidationError[]) {
  if (!mission || mission.waypoints.length < 2) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  const wps = mission.waypoints;
  const erroredLegs = new Set(
    errors
      .filter((e) => e.scope.kind === 'leg')
      .map((e) => (e.scope as { kind: 'leg'; legIndex: number }).legIndex),
  );
  const features: GeoJSON.Feature<GeoJSON.Polygon>[] = [];
  const baseDistM = Math.max(0, YAW_LINE_LENGTH_M - YAW_TIP_TRIANGLE_LEN_M);
  for (let i = 0; i < wps.length - 1; i++) {
    const lp = resolveEffectiveLegParams(wps, i);
    if (lp.gimbalYaw === undefined) continue;
    const mid = midpoint(
      [wps[i].lng, wps[i].lat],
      [wps[i + 1].lng, wps[i + 1].lat],
    );
    const apex = destinationPoint(mid, lp.gimbalYaw, YAW_LINE_LENGTH_M);
    const baseCenter = destinationPoint(mid, lp.gimbalYaw, baseDistM);
    const baseLeft = destinationPoint(
      baseCenter,
      (lp.gimbalYaw - 90 + 360) % 360,
      YAW_TIP_TRIANGLE_HALF_WIDTH_M,
    );
    const baseRight = destinationPoint(
      baseCenter,
      (lp.gimbalYaw + 90) % 360,
      YAW_TIP_TRIANGLE_HALF_WIDTH_M,
    );
    features.push({
      type: 'Feature',
      properties: {
        legIndex: i,
        waypointId: wps[i].id,
        recording: lp.recordVideo ? 1 : 0,
        hasError: erroredLegs.has(i) ? 1 : 0,
      },
      // GeoJSON polygon: first and last coord must match. Outer ring CCW.
      geometry: {
        type: 'Polygon',
        coordinates: [[apex, baseLeft, baseRight, apex]],
      },
    });
  }
  return { type: 'FeatureCollection' as const, features };
}

/** Build midpoint marker features for the selected mission's legs. */
function buildMidpointFeatures(mission: DemoMission | undefined, errors: ValidationError[]) {
  if (!mission || mission.waypoints.length < 2) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  const wps = mission.waypoints;
  const erroredLegs = new Set(
    errors
      .filter((e) => e.scope.kind === 'leg')
      .map((e) => (e.scope as { kind: 'leg'; legIndex: number }).legIndex),
  );
  const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
  for (let i = 0; i < wps.length - 1; i++) {
    const lp = resolveEffectiveLegParams(wps, i);
    const start: [number, number] = [wps[i].lng, wps[i].lat];
    const end: [number, number] = [wps[i + 1].lng, wps[i + 1].lat];
    const mid = midpoint(start, end);
    const tiltLabel = lp.gimbalPitch !== undefined ? `${Math.round(lp.gimbalPitch)}°` : '—';
    const intervalLabel = lp.intervalPhoto?.enabled
      ? `📷${lp.intervalPhoto.distanceM}m`
      : '';
    const recLabel = lp.recordVideo ? `REC ${lp.recordVideoLens ?? 'zoom'}` : '';
    const labelParts = [tiltLabel, recLabel, intervalLabel].filter(Boolean);
    features.push({
      type: 'Feature',
      properties: {
        legIndex: i,
        waypointId: wps[i].id,
        yawDeg: lp.gimbalYaw ?? 0,
        hasYaw: lp.gimbalYaw !== undefined ? 1 : 0,
        recording: lp.recordVideo ? 1 : 0,
        hasError: erroredLegs.has(i) ? 1 : 0,
        label: labelParts.join(' · '),
      },
      geometry: { type: 'Point', coordinates: mid },
    });
  }
  return { type: 'FeatureCollection' as const, features };
}

/** Build interval-capture-point preview features (small dots along the leg). */
function buildCaptureFeatures(mission: DemoMission | undefined) {
  if (!mission || mission.waypoints.length < 2) {
    return { type: 'FeatureCollection' as const, features: [] };
  }
  const wps = mission.waypoints;
  const features: GeoJSON.Feature<GeoJSON.Point>[] = [];
  // Cap to avoid pathological cases (extremely small interval on a long leg).
  const maxPerLeg = 200;
  for (let i = 0; i < wps.length - 1; i++) {
    const lp = resolveEffectiveLegParams(wps, i);
    if (!lp.intervalPhoto?.enabled || lp.intervalPhoto.distanceM <= 0) continue;
    if (lp.intervalPhoto.lenses.length === 0) continue;
    const start: [number, number] = [wps[i].lng, wps[i].lat];
    const end: [number, number] = [wps[i + 1].lng, wps[i + 1].lat];
    const total = haversineM(start, end);
    const step = lp.intervalPhoto.distanceM;
    let placed = 0;
    for (let d = step; d < total && placed < maxPerLeg; d += step) {
      const t = d / total;
      features.push({
        type: 'Feature',
        properties: { legIndex: i },
        geometry: {
          type: 'Point',
          coordinates: [
            start[0] + (end[0] - start[0]) * t,
            start[1] + (end[1] - start[1]) * t,
          ],
        },
      });
      placed++;
    }
  }
  return { type: 'FeatureCollection' as const, features };
}

function isMissing(map: MapboxMap): boolean {
  for (const id of ALL_SOURCES) if (!map.getSource(id)) return true;
  for (const id of ALL_LAYERS) if (!map.getLayer(id)) return true;
  return false;
}

function ensureSourcesAndLayers(map: MapboxMap): void {
  if (!map.getSource(SRC_MIDPOINTS)) {
    map.addSource(SRC_MIDPOINTS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_CAPTURES)) {
    map.addSource(SRC_CAPTURES, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_YAW_LINES)) {
    map.addSource(SRC_YAW_LINES, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getSource(SRC_YAW_TIPS)) {
    map.addSource(SRC_YAW_TIPS, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(LAYER_YAW_LINE)) {
    // Dashed pointer that emanates from the leg midpoint along the gimbal
    // yaw. The dash pattern makes it visually distinct from the solid
    // amber home-leg lines and the dashed route line (different dash
    // proportions). Color modulated on recording / error states so the
    // line tracks the midpoint marker's accent colour.
    map.addLayer({
      id: LAYER_YAW_LINE,
      type: 'line',
      source: SRC_YAW_LINES,
      paint: {
        'line-color': [
          'case',
          ['==', ['get', 'hasError'], 1], '#ef4444',
          ['==', ['get', 'recording'], 1], '#ef4444',
          '#22d3ee',
        ],
        'line-width': 2.5,
        'line-dasharray': [1.5, 1.5],
        'line-opacity': 0.95,
      },
    });
  }
  if (!map.getLayer(LAYER_YAW_TIP_FILL)) {
    // Filled triangle in real geo-space, base flush against the dashed
    // line's endpoint. Updates synchronously with the line during drag —
    // no symbol-placement lag.
    map.addLayer({
      id: LAYER_YAW_TIP_FILL,
      type: 'fill',
      source: SRC_YAW_TIPS,
      paint: {
        'fill-color': [
          'case',
          ['==', ['get', 'hasError'], 1], '#ef4444',
          ['==', ['get', 'recording'], 1], '#ef4444',
          '#22d3ee',
        ],
        'fill-opacity': 0.95,
        'fill-outline-color': 'rgba(0,0,0,0.85)',
      },
    });
  }
  if (!map.getLayer(LAYER_CAPS)) {
    // Tiny outlined dots at each planned interval-photo capture point.
    map.addLayer({
      id: LAYER_CAPS,
      type: 'circle',
      source: SRC_CAPTURES,
      paint: {
        'circle-radius': 3,
        'circle-color': '#fde047',
        'circle-stroke-width': 1,
        'circle-stroke-color': 'rgba(0,0,0,0.55)',
        'circle-opacity': 0.95,
      },
    });
  }
  if (!map.getLayer(LAYER_MID_BG)) {
    map.addLayer({
      id: LAYER_MID_BG,
      type: 'circle',
      source: SRC_MIDPOINTS,
      paint: {
        'circle-radius': 18,
        'circle-color': '#0f172a',
        'circle-stroke-width': [
          'case',
          ['==', ['get', 'hasError'], 1], 2,
          ['==', ['get', 'recording'], 1], 2,
          1.5,
        ],
        'circle-stroke-color': [
          'case',
          ['==', ['get', 'hasError'], 1], '#ef4444',
          ['==', ['get', 'recording'], 1], '#ef4444',
          'rgba(34, 211, 238, 0.85)',
        ],
        'circle-opacity': 0.9,
      },
    });
  }
  if (!map.getLayer(LAYER_MID_ARROW)) {
    // Arrow indicates gimbal yaw. ➤ glyph naturally points east — rotate
    // by yaw-90 to align with 0=north. When yaw is undefined the marker
    // shows a dot ('●') indicating "follow aircraft".
    map.addLayer({
      id: LAYER_MID_ARROW,
      type: 'symbol',
      source: SRC_MIDPOINTS,
      layout: {
        'text-field': [
          'case',
          ['==', ['get', 'hasYaw'], 1], '➤',
          '●',
        ],
        'text-size': 16,
        'text-rotate': ['-', ['coalesce', ['get', 'yawDeg'], 0], 90],
        'text-rotation-alignment': 'map',
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#22d3ee',
        'text-halo-color': 'rgba(0,0,0,0.75)',
        'text-halo-width': 1.4,
      },
    });
  }
  if (!map.getLayer(LAYER_MID_LABEL)) {
    map.addLayer({
      id: LAYER_MID_LABEL,
      type: 'symbol',
      source: SRC_MIDPOINTS,
      layout: {
        'text-field': ['get', 'label'],
        'text-size': 10,
        'text-anchor': 'top',
        'text-offset': [0, 1.6],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#e2e8f0',
        'text-halo-color': 'rgba(0,0,0,0.75)',
        'text-halo-width': 1.2,
      },
    });
  }
}

function pinLayersToTop(map: MapboxMap): void {
  for (const id of ALL_LAYERS) {
    try {
      if (map.getLayer(id)) map.moveLayer(id);
    } catch {
      // ignore
    }
  }
}

function teardownSourcesAndLayers(map: MapboxMap): void {
  for (const id of [...ALL_LAYERS].reverse()) {
    try {
      if (map.getLayer(id)) map.removeLayer(id);
    } catch {
      // ignore
    }
  }
  for (const id of ALL_SOURCES) {
    try {
      if (map.getSource(id)) map.removeSource(id);
    } catch {
      // ignore
    }
  }
}

/** Compute bearing from point a to point b in degrees [0, 360), 0=N. */
function bearingDeg(a: [number, number], b: [number, number]): number {
  const lng1 = (a[0] * Math.PI) / 180;
  const lat1 = (a[1] * Math.PI) / 180;
  const lng2 = (b[0] * Math.PI) / 180;
  const lat2 = (b[1] * Math.PI) / 180;
  const y = Math.sin(lng2 - lng1) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2)
    - Math.sin(lat1) * Math.cos(lat2) * Math.cos(lng2 - lng1);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

interface PopoverState {
  legIndex: number;
  waypointId: string;
  lngLat: [number, number];
}

export default function LegOverlayLayer() {
  const map = useMapStore((s) => s.map);
  const isMapReady = useMapStore((s) => s.isMapReady);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const updateWaypointLegParams = useDockDemoStore((s) => s.updateWaypointLegParams);

  // For the popover we render a portal at a Mapbox marker position.
  const [popover, setPopover] = useState<PopoverState | null>(null);
  const popoverHostRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<mapboxgl.Marker | null>(null);

  // Track drag state imperatively — useState updates would cause render
  // churn on every mousemove.
  const dragStateRef = useRef<{
    legIndex: number;
    waypointId: string;
    mid: [number, number];
    pointerMoved: boolean;
  } | null>(null);

  /** Refresh both sources from current store state. Safe to call when
   *  isStyleLoaded() is false because setData on existing sources is a
   *  no-op queue — only addSource / addLayer need a loaded style.
   *  Mirrors the safe pattern used elsewhere in dock-demo. */
  const apply = useCallback(() => {
    if (!map) return;
    const styleLoaded = map.isStyleLoaded();
    if (styleLoaded) ensureSourcesAndLayers(map);
    const s = useDockDemoStore.getState();
    const mission = s.missions.find((m) => m.id === s.selectedMissionId);
    const boundDock = mission && mission.dockStationId
      ? s.dockStations.find((d) => d.id === mission.dockStationId) ?? null
      : null;
    const errors = mission ? validateMission(mission, boundDock) : [];
    const midSrc = map.getSource(SRC_MIDPOINTS);
    const capSrc = map.getSource(SRC_CAPTURES);
    const yawSrc = map.getSource(SRC_YAW_LINES);
    const tipSrc = map.getSource(SRC_YAW_TIPS);
    if (midSrc && 'setData' in midSrc) {
      try { midSrc.setData(buildMidpointFeatures(mission, errors)); } catch { /* style mid-mutation */ }
    }
    if (capSrc && 'setData' in capSrc) {
      try { capSrc.setData(buildCaptureFeatures(mission)); } catch { /* same */ }
    }
    if (yawSrc && 'setData' in yawSrc) {
      try { yawSrc.setData(buildYawLineFeatures(mission, errors)); } catch { /* same */ }
    }
    if (tipSrc && 'setData' in tipSrc) {
      try { tipSrc.setData(buildYawTipFeatures(mission, errors)); } catch { /* same */ }
    }
    // Track the selected mission's route visibility so the leg overlay
    // (midpoint badges + gimbal arrows + interval-photo preview dots)
    // hides with the route. Source data stays populated either way so the
    // re-show toggle is a pure setLayoutProperty flip — no rebuild cost.
    const routeVisible = !!mission && mission.visible !== false;
    const visValue = routeVisible ? 'visible' : 'none';
    for (const id of ALL_LAYERS) {
      try {
        if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', visValue);
      } catch { /* layer may not exist yet */ }
    }
    if (styleLoaded) pinLayersToTop(map);
  }, [map]);

  // Lifecycle install + store subscription (rAF batched on missions changes).
  useEffect(() => {
    if (!map || !isMapReady) return;

    const repin = () => {
      if (isMissing(map)) {
        apply();
      } else {
        pinLayersToTop(map);
      }
    };

    apply();
    map.on('style.load', apply);
    map.on('styledata', repin);

    let prevMissions = useDockDemoStore.getState().missions;
    let prevSelected = useDockDemoStore.getState().selectedMissionId;
    let prevDockStations = useDockDemoStore.getState().dockStations;
    let rafId: number | null = null;
    const queueApply = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        apply();
      });
    };
    const unsubscribe = useDockDemoStore.subscribe(() => {
      const cur = useDockDemoStore.getState();
      // dockStations is included because validateMission(mission, dock) reads
      // dock-scoped no-fly zones + geofence — changes to those should refresh
      // on-map leg error highlighting.
      if (
        cur.missions !== prevMissions
        || cur.selectedMissionId !== prevSelected
        || cur.dockStations !== prevDockStations
      ) {
        prevMissions = cur.missions;
        prevSelected = cur.selectedMissionId;
        prevDockStations = cur.dockStations;
        queueApply();
      }
    });

    return () => {
      unsubscribe();
      if (rafId !== null) cancelAnimationFrame(rafId);
      map.off('style.load', apply);
      map.off('styledata', repin);
      teardownSourcesAndLayers(map);
    };
  }, [map, isMapReady, apply]);

  // Pointer interactions: click on a midpoint → popover; mousedown on a
  // midpoint → start drag-to-rotate; window-level mousemove/up complete it.
  useEffect(() => {
    if (!map || !isMapReady) return;

    const onMouseEnter = () => { map.getCanvas().style.cursor = 'grab'; };
    const onMouseLeave = () => {
      if (!dragStateRef.current) map.getCanvas().style.cursor = '';
    };

    const onMouseDown = (e: MapMouseEvent) => {
      const feature = e.features?.[0];
      if (!feature) return;
      const legIndex = Number(feature.properties?.legIndex);
      const waypointId = String(feature.properties?.waypointId ?? '');
      if (!Number.isFinite(legIndex) || !waypointId) return;
      const cur = useDockDemoStore.getState();
      const mission = cur.missions.find((m) => m.id === cur.selectedMissionId);
      if (!mission) return;
      const wps = mission.waypoints;
      if (legIndex >= wps.length - 1) return;
      const mid = midpoint(
        [wps[legIndex].lng, wps[legIndex].lat],
        [wps[legIndex + 1].lng, wps[legIndex + 1].lat],
      );
      dragStateRef.current = { legIndex, waypointId, mid, pointerMoved: false };
      map.getCanvas().style.cursor = 'grabbing';
      // Prevent Mapbox's own panning during the drag.
      e.preventDefault();
    };

    const onClick = (e: MapMouseEvent) => {
      // Click handler only fires on mouseup over the same feature when no
      // significant drag happened. We use dragStateRef.pointerMoved to
      // distinguish drag-rotate from click-popover; if the user dragged
      // even a small amount the click is suppressed.
      const ds = dragStateRef.current;
      if (ds && ds.pointerMoved) return;
      const feature = e.features?.[0];
      if (!feature || feature.geometry.type !== 'Point') return;
      const legIndex = Number(feature.properties?.legIndex);
      const waypointId = String(feature.properties?.waypointId ?? '');
      if (!Number.isFinite(legIndex) || !waypointId) return;
      const coords = feature.geometry.coordinates as [number, number];
      setPopover({ legIndex, waypointId, lngLat: coords });
    };

    // Bind the same handlers to BOTH the midpoint shell AND the yaw-tip
    // hitbox. Either acts as a grab handle for the drag-to-rotate
    // operation; the popover opens regardless of which one the user
    // clicked. Drag math is identical (bearing from leg midpoint to
    // cursor) so the feature.properties.legIndex + waypointId — which
    // both layers expose — is the only thing the handler needs.
    for (const layerId of DRAG_LAYER_IDS) {
      map.on('mouseenter', layerId, onMouseEnter);
      map.on('mouseleave', layerId, onMouseLeave);
      map.on('mousedown', layerId, onMouseDown);
      map.on('click', layerId, onClick);
    }

    return () => {
      for (const layerId of DRAG_LAYER_IDS) {
        map.off('mouseenter', layerId, onMouseEnter);
        map.off('mouseleave', layerId, onMouseLeave);
        map.off('mousedown', layerId, onMouseDown);
        map.off('click', layerId, onClick);
      }
    };
  }, [map, isMapReady]);

  // Window-level mousemove/up for the drag-rotate. Active only while a
  // dragStateRef is set; reads the map directly to convert screen pixel
  // motion into a fresh bearing from the leg midpoint.
  useEffect(() => {
    if (!map) return;
    const onMove = (ev: MouseEvent) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      ds.pointerMoved = true;
      // Map cursor coords to lng/lat to compute bearing from the leg
      // midpoint to the cursor. Then convert to gimbal yaw (0=N) and
      // optionally snap to 5° with Shift.
      const rect = map.getCanvas().getBoundingClientRect();
      const cursorLngLat = map.unproject([
        ev.clientX - rect.left,
        ev.clientY - rect.top,
      ]);
      const cursorCoords: [number, number] = [cursorLngLat.lng, cursorLngLat.lat];
      let yaw = bearingDeg(ds.mid, cursorCoords);
      if (ev.shiftKey) yaw = Math.round(yaw / 5) * 5;
      yaw = ((yaw % 360) + 360) % 360;
      const cur = useDockDemoStore.getState();
      const mission = cur.missions.find((m) => m.id === cur.selectedMissionId);
      if (!mission) return;
      updateWaypointLegParams(mission.id, ds.waypointId, { gimbalYaw: yaw });
    };
    const onUp = () => {
      if (!dragStateRef.current) return;
      dragStateRef.current = null;
      map.getCanvas().style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [map, updateWaypointLegParams]);

  // Popover: anchor a DOM marker at the leg midpoint. The marker hosts
  // a portal div that React renders into; the popover content is a React
  // tree that reads/writes the leg params via the store.
  useEffect(() => {
    if (!map || !popover) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }
    if (!popoverHostRef.current) {
      popoverHostRef.current = document.createElement('div');
      popoverHostRef.current.className = 'dock-demo-leg-popover-host';
    }
    if (!markerRef.current) {
      markerRef.current = new mapboxgl.Marker({
        element: popoverHostRef.current,
        anchor: 'bottom',
        offset: [0, -28],
      });
    }
    markerRef.current.setLngLat(popover.lngLat).addTo(map);
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [map, popover]);

  // Close popover when clicking outside its host element.
  useEffect(() => {
    if (!popover) return;
    const onClickAway = (ev: MouseEvent) => {
      const host = popoverHostRef.current;
      if (!host) return;
      if (host.contains(ev.target as Node)) return;
      // Don't close immediately on the canvas-click that opened it.
      if ((ev.target as HTMLElement | null)?.tagName === 'CANVAS') return;
      setPopover(null);
    };
    window.addEventListener('mousedown', onClickAway);
    return () => window.removeEventListener('mousedown', onClickAway);
  }, [popover]);

  // Close popover on selection change (so a new mission doesn't show a
  // stale popup anchored to the old leg).
  useEffect(() => {
    setPopover(null);
  }, [selectedMissionId]);

  return (
    <>
      {popover && popoverHostRef.current && (
        createPortal(<LegPopoverContents popover={popover} onClose={() => setPopover(null)} />, popoverHostRef.current)
      )}
    </>
  );
}

/* ─── Popover contents (compact form, mirrors MissionLegEditor) ────── */

function LegPopoverContents({
  popover,
  onClose,
}: {
  popover: PopoverState;
  onClose: () => void;
}) {
  const updateWaypointLegParams = useDockDemoStore((s) => s.updateWaypointLegParams);
  const selectedMissionId = useDockDemoStore((s) => s.selectedMissionId);
  const missions = useDockDemoStore((s) => s.missions);
  const mission = missions.find((m) => m.id === selectedMissionId);
  if (!mission) return null;
  const wp = mission.waypoints.find((w) => w.id === popover.waypointId);
  if (!wp) return null;
  const lp: DemoLegParams = wp.legParams ?? {};
  const interval = lp.intervalPhoto ?? {
    enabled: false,
    distanceM: 5,
    lenses: ['wide', 'ir'] as DemoPhotoPayload[],
  };

  const set = (patch: Partial<DemoLegParams>) => {
    updateWaypointLegParams(mission.id, popover.waypointId, patch);
  };

  return (
    <div className="dock-demo-leg-popover" onMouseDown={(e) => e.stopPropagation()}>
      <div className="dock-demo-leg-popover__head">
        <span>Leg {popover.legIndex + 1} → {popover.legIndex + 2}</span>
        <button
          type="button"
          className="dock-demo-leg-popover__close"
          onClick={onClose}
          aria-label="Close"
        >×</button>
      </div>

      <div className="dock-demo-leg-popover__row">
        <label>
          Yaw (°)
          <input
            type="number"
            min={0}
            max={360}
            step={1}
            placeholder="auto"
            value={lp.gimbalYaw ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return set({ gimbalYaw: undefined });
              set({ gimbalYaw: ((Number(raw) % 360) + 360) % 360 });
            }}
          />
        </label>
        <label>
          Pitch (°)
          <input
            type="number"
            min={-90}
            max={30}
            step={1}
            placeholder="auto"
            value={lp.gimbalPitch ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') return set({ gimbalPitch: undefined });
              set({ gimbalPitch: Number(raw) });
            }}
          />
        </label>
      </div>

      <label className="dock-demo-leg-popover__check">
        <input
          type="checkbox"
          checked={!!lp.recordVideo}
          onChange={(e) =>
            set({
              recordVideo: e.target.checked,
              recordVideoLens: e.target.checked ? (lp.recordVideoLens ?? 'zoom') : lp.recordVideoLens,
            })
          }
        />
        Record video
        {lp.recordVideo && (
          <select
            value={lp.recordVideoLens ?? 'zoom'}
            onChange={(e) => set({ recordVideoLens: e.target.value as DemoPhotoPayload })}
          >
            {PAYLOADS.map((p) => (<option key={p} value={p}>{p}</option>))}
          </select>
        )}
      </label>

      <label className="dock-demo-leg-popover__check">
        <input
          type="checkbox"
          checked={interval.enabled}
          onChange={(e) =>
            set({ intervalPhoto: { ...interval, enabled: e.target.checked } })
          }
        />
        Interval
        {interval.enabled && (
          <>
            <input
              type="number"
              className="dock-demo-leg-popover__interval-dist"
              min={1}
              max={500}
              step={1}
              value={interval.distanceM}
              onChange={(e) =>
                set({
                  intervalPhoto: {
                    ...interval,
                    distanceM: Number(e.target.value) || 0,
                  },
                })
              }
            />
            <span className="dock-demo-leg-popover__interval-unit">m</span>
            <div className="dock-demo-leg-popover__lenses">
              {PAYLOADS.map((p) => (
                <label key={p}>
                  <input
                    type="checkbox"
                    checked={interval.lenses.includes(p)}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? Array.from(new Set([...interval.lenses, p]))
                        : interval.lenses.filter((x) => x !== p);
                      set({ intervalPhoto: { ...interval, lenses: next } });
                    }}
                  />
                  {p}
                </label>
              ))}
            </div>
          </>
        )}
      </label>
    </div>
  );
}
